import { CheckCircle2, XCircle, AlertTriangle, TrendingUp, Heart, DollarSign, User } from "lucide-react";

const getSeverityStyle = (severity) => {
  if (severity === "alto") return "bg-[#EF4444]/10 text-[#EF4444]";
  if (severity === "medio") return "bg-[#F59E0B]/10 text-[#F59E0B]";
  return "bg-[#10B981]/10 text-[#10B981]";
};

const getImpactStyle = (impact) => {
  if (impact === "alto") return "bg-[#10B981]/10 text-[#10B981]";
  if (impact === "medio") return "bg-[#002FA7]/10 text-[#002FA7]";
  return "bg-[#6B7280]/10 text-[#6B7280]";
};

const getOutlookColor = (outlook) => {
  if (outlook === "positivo") return "#10B981";
  if (outlook === "negativo") return "#EF4444";
  return "#F59E0B";
};

export const ProbabilitySection = ({ simulation, successData, PieChart, Pie, Cell, ResponsiveContainer }) => (
  <div className="bg-white rounded-3xl p-8 animate-fade-in stagger-1" data-testid="probability-card">
    <h2 className="font-['Outfit'] text-xl font-semibold text-[#111111] mb-6">Probabilidad de exito</h2>
    <div className="flex flex-col md:flex-row items-center gap-8">
      <div className="w-48 h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart><Pie data={successData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value">{successData.map((entry) => (<Cell key={`cell-${entry.name}`} fill={entry.fill} />))}</Pie></PieChart>
        </ResponsiveContainer>
        <div className="text-center -mt-28">
          <p className="text-4xl font-['Outfit'] font-bold text-[#111111]">{simulation.success_probability}%</p>
          <p className="text-sm text-[#6B7280]">de exito</p>
        </div>
      </div>
      <div className="flex-1 space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#10B981]/10 flex items-center justify-center"><CheckCircle2 className="w-6 h-6 text-[#10B981]" /></div>
          <div><p className="font-semibold text-[#111111]">Escenario favorable</p><p className="text-sm text-[#6B7280]">{simulation.success_probability}% probabilidad</p></div>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#EF4444]/10 flex items-center justify-center"><XCircle className="w-6 h-6 text-[#EF4444]" /></div>
          <div><p className="font-semibold text-[#111111]">Escenario desfavorable</p><p className="text-sm text-[#6B7280]">{simulation.failure_probability}% probabilidad</p></div>
        </div>
      </div>
    </div>
  </div>
);

export const RisksBenefitsSection = ({ simulation }) => (
  <div className="grid md:grid-cols-2 gap-6">
    <div className="bg-white rounded-3xl p-6 animate-fade-in stagger-2" data-testid="risks-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#EF4444]/10 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-[#EF4444]" /></div>
        <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111]">Riesgos</h3>
      </div>
      <div className="space-y-3">
        {simulation.risks?.map((risk) => (
          <div key={risk.name} className="p-3 bg-[#F5F5F7] rounded-xl">
            <div className="flex items-center justify-between mb-1"><span className="font-medium text-[#111111] text-sm">{risk.name}</span><span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityStyle(risk.severity)}`}>{risk.severity}</span></div>
            <p className="text-xs text-[#6B7280]">{risk.description}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-white rounded-3xl p-6 animate-fade-in stagger-3" data-testid="benefits-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-[#10B981]" /></div>
        <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111]">Beneficios</h3>
      </div>
      <div className="space-y-3">
        {simulation.benefits?.map((benefit) => (
          <div key={benefit.name} className="p-3 bg-[#F5F5F7] rounded-xl">
            <div className="flex items-center justify-between mb-1"><span className="font-medium text-[#111111] text-sm">{benefit.name}</span><span className={`text-xs px-2 py-0.5 rounded-full ${getImpactStyle(benefit.impact)}`}>{benefit.impact}</span></div>
            <p className="text-xs text-[#6B7280]">{benefit.description}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const TimelineSection = ({ simulation }) => {
  const periods = [
    { label: "Corto plazo", period: "1-3 meses", data: simulation.short_term },
    { label: "Medio plazo", period: "3-12 meses", data: simulation.medium_term },
    { label: "Largo plazo", period: "+1 ano", data: simulation.long_term }
  ];

  return (
    <div className="bg-white rounded-3xl p-6 animate-fade-in stagger-4" data-testid="timeline-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 flex items-center justify-center"><span className="text-[#002FA7]">&#128339;</span></div>
        <h3 className="font-['Outfit'] text-lg font-semibold text-[#111111]">Proyeccion temporal</h3>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {periods.map((item) => {
          const outlookColor = getOutlookColor(item.data?.outlook);
          const confidence = item.data?.confidence || 50;
          return (
            <div key={item.label} className="p-4 bg-[#F5F5F7] rounded-2xl">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-[#111111]">{item.label}</span>
                <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: `${outlookColor}15`, color: outlookColor }}>{item.data?.outlook || "neutro"}</span>
              </div>
              <p className="text-xs text-[#6B7280] mb-2">{item.period}</p>
              <p className="text-sm text-[#111111]">{item.data?.description}</p>
              <div className="mt-3 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-[#E5E5EA] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${confidence}%`, backgroundColor: outlookColor }} /></div>
                <span className="text-xs text-[#6B7280]">{confidence}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ImpactCards = ({ simulation }) => (
  <div className="grid md:grid-cols-3 gap-4 animate-fade-in stagger-5">
    <div className="bg-white rounded-2xl p-5" data-testid="emotional-impact">
      <div className="flex items-center gap-2 mb-3"><Heart className="w-5 h-5 text-[#EC4899]" /><span className="font-medium text-[#111111]">Impacto emocional</span></div>
      <div className="space-y-2">
        <div className="flex justify-between items-center"><span className="text-xs text-[#6B7280]">Positivo</span><span className="text-sm font-medium text-[#10B981]">{simulation.emotional_impact?.positive}%</span></div>
        <div className="flex justify-between items-center"><span className="text-xs text-[#6B7280]">Negativo</span><span className="text-sm font-medium text-[#EF4444]">{simulation.emotional_impact?.negative}%</span></div>
      </div>
    </div>
    <div className="bg-white rounded-2xl p-5" data-testid="financial-impact">
      <div className="flex items-center gap-2 mb-3"><DollarSign className="w-5 h-5 text-[#10B981]" /><span className="font-medium text-[#111111]">Impacto financiero</span></div>
      <div className="space-y-2">
        <div className="flex justify-between items-center"><span className="text-xs text-[#6B7280]">Positivo</span><span className="text-sm font-medium text-[#10B981]">{simulation.financial_impact?.positive}%</span></div>
        <div className="flex justify-between items-center"><span className="text-xs text-[#6B7280]">Negativo</span><span className="text-sm font-medium text-[#EF4444]">{simulation.financial_impact?.negative}%</span></div>
      </div>
    </div>
    <div className="bg-white rounded-2xl p-5" data-testid="personal-impact">
      <div className="flex items-center gap-2 mb-3"><User className="w-5 h-5 text-[#5B21B6]" /><span className="font-medium text-[#111111]">Impacto personal</span></div>
      <div className="space-y-2">
        <div className="flex justify-between items-center"><span className="text-xs text-[#6B7280]">Crecimiento</span><span className="text-sm font-medium text-[#10B981]">{simulation.personal_impact?.growth}%</span></div>
        <div className="flex justify-between items-center"><span className="text-xs text-[#6B7280]">Estres</span><span className="text-sm font-medium text-[#EF4444]">{simulation.personal_impact?.stress}%</span></div>
      </div>
    </div>
  </div>
);
